import {
  StargraveLeader,
  StargraveLeaderRole,
  StargraveStat,
  StargraveStatLine,
} from '../types';
import {
  stargraveBackgrounds,
  stargraveLeaderRules,
  stargravePowers,
} from '../data/stargrave/coreRules';
import { getAllStargraveEquipment } from '../stores/stargraveCustomStore';
import { StatModifier } from '../utils/splitStats';

/** Stat modifiers from carried gear (armour bonuses, weapon move penalties).
 *  Heavy/combat armour negates rapid-fire and flamethrower Move penalties. */
export function getStargraveGearStatModifiers(gearIds: string[], stat: StargraveStat): StatModifier[] {
  const equipmentById = new Map(getAllStargraveEquipment().map((item) => [item.id, item]));
  const gear = gearIds.flatMap((id) => equipmentById.get(id) ?? []);
  const hasHeavyOrCombatArmour = gearIds.includes('heavy-armour') || gearIds.includes('combat-armour');
  return gear.flatMap((item) => {
    const value = stat === 'armour' ? item.armourModifier : stat === 'move' ? item.moveModifier : undefined;
    if (value === undefined || value === 0) return [];
    if (stat === 'move' && item.category === 'weapon' && hasHeavyOrCombatArmour) return [];
    return [{ label: item.name, value, type: 'add' as const }];
  });
}

export function applyStargraveGearModifiers(stats: StargraveStatLine, gearIds: string[]): StargraveStatLine {
  const result = { ...stats };
  for (const stat of ['move', 'armour'] as StargraveStat[]) {
    result[stat] += getStargraveGearStatModifiers(gearIds, stat).reduce((sum, modifier) => sum + modifier.value, 0);
  }
  return result;
}

export interface CreateStargraveLeaderInput {
  role: StargraveLeaderRole;
  name: string;
  backgroundId: string;
  statChoices: StargraveStat[];
  powerIds: string[];
  improvedPowerIds: string[];
  gearIds: string[];
}

const addModifiers = (
  base: StargraveStatLine,
  fixed: Partial<StargraveStatLine>,
  choices: StargraveStat[]
): StargraveStatLine => {
  const stats = { ...base };
  for (const stat of Object.keys(fixed) as StargraveStat[]) {
    stats[stat] += fixed[stat] ?? 0;
  }
  for (const stat of choices) stats[stat] += 1;
  return stats;
};

export function buildStargraveLeader(input: CreateStargraveLeaderInput): StargraveLeader {
  const rules = stargraveLeaderRules[input.role];
  const background = stargraveBackgrounds.find((candidate) => candidate.id === input.backgroundId);
  if (!input.name.trim()) throw new Error('Name is required.');
  if (!background) throw new Error('Select a valid background.');

  const statChoices = Array.from(new Set(input.statChoices));
  if (
    statChoices.length !== background.choice.count
    || statChoices.some((stat) => !background.choice.options.includes(stat))
  ) {
    throw new Error(`Choose ${background.choice.count} valid stat improvement${background.choice.count === 1 ? '' : 's'}.`);
  }

  const powerIds = Array.from(new Set(input.powerIds));
  if (powerIds.length !== rules.startingPowers) {
    throw new Error(`Choose exactly ${rules.startingPowers} powers.`);
  }
  const powerMap = new Map(stargravePowers.map((power) => [power.id, power]));
  if (powerIds.some((id) => !powerMap.has(id))) throw new Error('One or more selected powers are invalid.');

  const coreCount = powerIds.filter((id) => background.corePowerIds.includes(id)).length;
  if (coreCount < rules.corePowerRange[0] || coreCount > rules.corePowerRange[1]) {
    throw new Error(`Choose ${rules.corePowerRange[0]} or ${rules.corePowerRange[1]} core powers.`);
  }

  const improvedPowerIds = Array.from(new Set(input.improvedPowerIds));
  if (improvedPowerIds.length !== rules.activationImprovements) {
    throw new Error(`Choose exactly ${rules.activationImprovements} powers to improve.`);
  }
  if (improvedPowerIds.some((id) => !powerIds.includes(id))) {
    throw new Error('Only selected powers can be improved.');
  }

  const equipmentMap = new Map(getAllStargraveEquipment().map((item) => [item.id, item]));
  const gear = input.gearIds.map((id) => equipmentMap.get(id));
  if (gear.some((item) => !item)) throw new Error('One or more selected equipment items are invalid.');
  const occupiedSlots = gear.reduce((total, item) => total + (item?.gearSlots ?? 0), 0);
  if (occupiedSlots > rules.gearSlots) throw new Error(`Gear exceeds the ${rules.gearSlots}-slot limit.`);
  if (gear.filter((item) => item?.category === 'armour').length > 1) {
    throw new Error('A leader may wear only one type of armour.');
  }
  if (input.gearIds.includes('shield') && gear.some((item) => item?.category === 'weapon' && item.gearSlots >= 2)) {
    throw new Error('A shield cannot be carried with a weapon that uses 2 or more slots.');
  }

  return {
    id: crypto.randomUUID(),
    role: input.role,
    name: input.name.trim(),
    backgroundId: background.id,
    stats: addModifiers(rules.baseStats, background.fixedModifiers, statChoices),
    level: rules.level,
    gearIds: Array.from(new Set(input.gearIds)),
    permanentInjuryIds: [],
    powers: powerIds.map((powerId) => {
      const selectedPower = powerMap.get(powerId)!;
      const isCore = background.corePowerIds.includes(powerId);
      const modifier = isCore ? rules.coreActivationModifier : rules.nonCoreActivationModifier;
      return {
        powerId,
        activation: selectedPower.activation + modifier - (improvedPowerIds.includes(powerId) ? 1 : 0),
        strain: selectedPower.strain,
      };
    }),
    gameState: {
      currentHealth: addModifiers(rules.baseStats, background.fixedModifiers, statChoices).health,
      stunned: false,
      poisoned: false,
      loot: null,
      leftTable: false,
      activeBuffs: [],
    },
  };
}
